import axios from 'axios';

// Get API URL and token from environment variables
const API_URL = import.meta.env.VITE_LYRA_API_URL || 'https://fa-lyra.companion.at';
const API_TOKEN = import.meta.env.VITE_LYRA_API_TOKEN || '';

// Create API instance with authorization header
const lyraApi = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${API_TOKEN}`
  }
});

// Keep track of chats for which the transcript has already been sent
const transcriptSentChats = new Set<string>();

/**
 * Create a new chat session
 * @param name Optional name for the chat session
 * @returns The chat ID for the new session
 */
export const createChat = async (name: string = 'PeerTube Chat'): Promise<string> => {
  try {
    const response = await lyraApi.post('/chat', { name });
    return response.data.chat_id;
  } catch (error) {
    console.error('Error creating chat:', error);
    throw new Error('Failed to create chat session');
  }
};

/**
 * Send a question to the chat and get a streaming response
 * @param chatId The chat ID
 * @param question The question to ask
 * @param videoContext Context information about the video
 * @param onChunkReceived Callback function to handle each chunk of the streaming response
 */
export const streamQuestion = async (
  chatId: string, 
  question: string, 
  videoContext: any,
  onChunkReceived: (text: string) => void
): Promise<void> => {
  try {
    // Create the request URL
    const url = `${API_URL}/chat/${chatId}/question/stream`;
    
    // Decide whether to include the transcript (only for the very first message of a chat)
    const includeTranscript = videoContext?.transcript && !transcriptSentChats.has(chatId);

    // Development-only logging
    if (import.meta.env.DEV) {
      if (includeTranscript) {
        console.log('Including transcript in first message for chat:', chatId);
      } else {
        console.log('Transcript will not be sent (already sent once or not available).');
      }
    }
    
    // Prepare the request body with max_tokens as specified
    // Combine the question and context into a single string for better processing
    let enhancedQuestion = question;
    
    // Build the enhanced question depending on whether the transcript should be included
    if (includeTranscript) {
      enhancedQuestion = `Hier ist das Transkript des Videos:\n\n${videoContext.transcript}\n\nBasierend auf diesem Transkript, ${question}, Formatiere die antwort strukturiert und mit emoticons`;
      // Mark transcript as sent for this chat so future questions omit it
      transcriptSentChats.add(chatId);
    } else {
      // If no transcript (or it has already been sent), use other video metadata
      const metadataContext = `Titel: ${videoContext.title}\nBeschreibung: ${videoContext.description}\nKanal: ${videoContext.channel}\nUploader: ${videoContext.uploader}\nVeröffentlicht: ${videoContext.publishDate}\n\n`;
      enhancedQuestion = `Hier sind die Metadaten des Videos:\n\n${metadataContext}\nBasierend auf diesen Informationen, ${question}, Formatiere die antwort strukturiert und mit emoticons`;
    }
    
    // Keep the original context for reference
    const data = {
      question: enhancedQuestion,  // Send the enhanced question with context
      model: "GPT41mini",  // Use GPT41 model as requested
      max_tokens: 128000, // Set max_tokens to 32000 as requested
      temperature: 0.7,  // Default temperature
    };
    
    // Development-only: log full context being sent to the API for debugging
    if (import.meta.env.DEV) {
      console.log('Sending to API with context:', JSON.stringify(videoContext, null, 2));
    }

    // Make the fetch request with streaming response
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${API_TOKEN}`
      },
      body: JSON.stringify(data)
    });

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    // Process the streaming response
    const reader = response.body!.getReader();
    const decoder = new TextDecoder();
    let partialChunk = ''; // Puffer für unvollständige Chunks
    
    while (true) {
      const { done, value } = await reader.read();
      
      if (done) {
        // Verarbeite verbleibende Daten im Puffer, falls vorhanden
        if (partialChunk.trim()) {
          processChunk(partialChunk);
        }
        break;
      }
      
      // Decode the chunk and add to the partial chunk buffer
      const chunk = decoder.decode(value, { stream: true });
      partialChunk += chunk;
      
      // Teile den Puffer an 'data:' auf und behalte den letzten Teil als neuen Puffer
      const parts = partialChunk.split('data:');
      // Der letzte Teil könnte unvollständig sein, daher speichern wir ihn für den nächsten Durchlauf
      partialChunk = parts.pop() || '';
      
      // Verarbeite alle vollständigen Teile
      for (const part of parts) {
        if (part.trim()) {
          processChunk('data:' + part);
        }
      }
    }
    
    // Hilfsfunktion zur Verarbeitung eines einzelnen Chunks
    function processChunk(chunk: string) {
      // Normalisiere den Chunk, um sicherzustellen, dass jedes "data:" in einer neuen Zeile steht
      const normalizedChunk = chunk.replace(/data:/g, '\ndata:').trim();
      const lines = normalizedChunk.split('\n');
      
      for (const line of lines) {
        if (line.startsWith('data:')) {
          try {
            // Extrahiere den JSON-Teil
            const jsonStr = line.substring(5).trim();
            if (!jsonStr) continue;
            
            const data = JSON.parse(jsonStr);
            
            // Extrahiere den Text der Antwort
            if (data.assistant !== undefined) {
              // Sende den Text an den Callback
              onChunkReceived(data.assistant);
            }
          } catch (error) {
            // Wenn das JSON-Parsing fehlschlägt, versuche den Text mit einem regulären Ausdruck zu extrahieren
            try {
              const jsonStr = line.substring(5).trim();
              // Suche nach dem assistant-Feld mit einem regulären Ausdruck
              // Dieser Ausdruck ist speziell für das Format der API optimiert
              const match = jsonStr.match(/"assistant"\s*:\s*"([^"]*)"/i);
              if (match && match[1]) {
                onChunkReceived(match[1]);
              } else {
                // Versuche einen aggressiveren Fallback, um Text zu extrahieren
                const simpleMatch = jsonStr.match(/"assistant"\s*:\s*([^,}]+)/i);
                if (simpleMatch && simpleMatch[1]) {
                  // Entferne Anführungszeichen, falls vorhanden
                  let extractedText = simpleMatch[1].trim();
                  if (extractedText.startsWith('"') && extractedText.endsWith('"')) {
                    extractedText = extractedText.substring(1, extractedText.length - 1);
                  }
                  onChunkReceived(extractedText);
                }
              }
            } catch (extractError) {
              // Nur im Entwicklungsmodus protokollieren
              if (import.meta.env.DEV) {
                console.debug('Fehler beim Extrahieren des Textes:', extractError);
              }
            }
          }
        }
      }
    }
  } catch (error: unknown) {
    if (error instanceof Error) {
      console.error('Error streaming question:', error.message);
    } else {
      console.error('Error streaming question:', String(error));
    }
    throw error;
  }
};

export default lyraApi;
